import { User } from "@phosphor-icons/react"
import { forwardRef, type HTMLAttributes } from "react"
import { twMerge } from "tailwind-merge"
import { Icon } from "../icon/Icon"

export type AvatarProps = HTMLAttributes<HTMLDivElement> & {
  className?: string
  /** Rendered as the aria-label for screen readers */
  label?: string
  size?: "xsmall" | "small" | "medium" | "large" | "xlarge"
} & (
    | {
        src: string
        initials?: never
        icon?: never
      }
    | {
        src?: never
        initials: string
        icon?: never
      }
    | {
        src?: never
        initials?: never
        icon?: React.ElementType
      }
  )

/** A visual representation of a user. Can be an image, initials, or an icon.
 *
 * ```tsx
 * import { User } from "@phosphor-icons/react"
 * import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
 *
 * <Avatar src="https://example.com/avatar.jpg" />
 * <Avatar initials="TT" />
 * <Avatar icon={User} />
 * ```
 */

const sizeMap = {
  xsmall: "20px",
  small: "32px",
  medium: "48px",
  large: "72px",
  xlarge: "124px",
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      icon = User,
      initials,
      label = "Avatar",
      size = "medium",
      style,
      ...props
    }: AvatarProps,
    ref
  ) => {
    let children = null
    if (props.src) {
      const { src } = props
      children = (
        // Should probably use next/image here, though not sure how to determine
        // the width and height.
        <img aria-hidden="true" src={src} className="w-full h-full" />
      )
    } else if (initials) {
      children = initials
    } else {
      children = <Icon as={icon} className="w-1/2 h-1/2" />
    }

    return (
      <div
        ref={ref}
        role="img"
        aria-label={label}
        data-size={size}
        className={twMerge(
          "flex items-center justify-center rounded-full font-semibold uppercase tracking-[0.03em] text-neutral-400 dark:text-neutral-500 bg-neutral-900/10 dark:bg-neutral-100/10 h-(--size) w-(--size) text-[calc(var(--size)/2.5)]",
          className
        )}
        style={{
          // @ts-expect-error - inline css variable
          "--size": sizeMap[size],
          ...style,
        }}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Avatar.displayName = "Avatar"
